import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import Title from 'Components/ComponentLibrary/Atoms/Text/Title';
import Subtitle from 'Components/ComponentLibrary/Atoms/Text/Subtitle';
import useMobileLayout from 'Hooks/useMobileLayout';
import {
  ResourceSummaryGroups,
} from 'Components/ComponentLibrary/Components/Uncategorized/ResourceSummaryCard/ResourceSummaryGroup';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import PlaidButton from 'Components/UI/PlaidButton';
import Button from 'Components/UI/Button';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import usePlaidLink from 'Hooks/usePlaidLink';
import { plaidModal } from 'Consts/plaid';
import PropTypes from 'prop-types';


const useStylesForDisabledOverlay = makeStyles((theme) => ({
  disabledOverlayRoot: {
    position: 'relative',
    width: '100%',
  },
  overlay: {
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      borderRadius: theme.radius.md,
      zIndex: 10,
      filter: 'blur(5px)',
      background: 'rgba(255, 255, 255, 0.8)',
      boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
    },
  },
}));

export function DisabledOverlay({ disabled, children }) {
  const classes = useStylesForDisabledOverlay();
  return (
    <Box test={disabled ? 'overlay-active' : 'overlay-inactive'} className={classNames([classes.disabledOverlayRoot, disabled && classes.overlay])}>
      {children}
    </Box>
  );
}
DisabledOverlay.defaultProps = {
  disabled: false,
};
DisabledOverlay.propTypes = {
  disabled: PropTypes.bool,
  children: PropTypes.node.isRequired,
};


const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: plaidModal.width,
    height: plaidModal.maxHeight,
    margin: 'auto',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: theme.spacing(10),
  },
  rootDesktop: {
    maxWidth: '600px',
  },
  mainContent: {
    width: '100%',
    flex: 1,
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3),
  },
  subtitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(3.5),
  },
}));

const ApexFundingAccountListStep = () => {
  const { resource } = useMetaLayoutContext();
  const { loadModalCollectionByLink, refreshModalCollection } = useModalContext();
  const { refreshPageContentCollection } = usePageContentContext();
  const isMobile = useMobileLayout();
  const classes = useStyles();

  const [activeMicroDepositPlaidTokenURL, setActiveMicroDepositPlaidTokenURL] = useState(null);
  const clearActiveMicroDepositPlaidTokenURL = () => setActiveMicroDepositPlaidTokenURL(null);

  const [isPlaidFlowActive, setIsPlaidFlowActive] = useState(false);
  const handlePlaidFlowStart = () => setIsPlaidFlowActive(true);
  const handlePlaidFlowStop = () => {
    clearActiveMicroDepositPlaidTokenURL();
    setIsPlaidFlowActive(false);
  };

  async function refresh() {
    await refreshModalCollection();
    handlePlaidFlowStop();
  }


  async function initiateApexFundingAccountLinking(apexFundingAccountLink) {
    try {
      if (Number(resource.attr('linkedAccountsCount')) >= 2) {
        await refresh();
        return;
      }
      const [apexFundingAccount] = await apexFundingAccountLink.post({}, { deprecatedAllowAlterOfRelation: true });

      if (apexFundingAccount.relationship(fr.relationships.plaidToken.name)) {
        setActiveMicroDepositPlaidTokenURL(apexFundingAccount.relationship(fr.relationships.plaidToken.name).link('related').url());
      } else {
        loadModalCollectionByLink(apexFundingAccount.relationship(fr.relationships.apexFundingAccountLinking.name).link('related').url());
      }
    } catch {
      await refresh();
    } finally {
      await refreshPageContentCollection();
    }
  }

  async function handleClickForAccount({ action }) {
    if (action.name === 'link') {
      await initiateApexFundingAccountLinking(action.link);
    } else if (action.name === 'sync' || action.name === 'verify') {
      loadModalCollectionByLink(action.link.url());
    } else if (action.name === 'plaidToken') {
      setActiveMicroDepositPlaidTokenURL(action.link.url());
    }
  }
  function handleClickForAccountAction({ action }) {
    if (['unlink', 'delete'].includes(action.id)) {
      loadModalCollectionByLink(action.link.url());
    }
  }

  async function handlePlaidAddAccountSuccess(exchangeTokenResp) {
    const [linkedAccount] = await exchangeTokenResp.resources().head().relationship(fr.relationships.linkedApexFundingAccounts.name).link('related')
      .get();

    await initiateApexFundingAccountLinking(linkedAccount.link('self'));
  }

  async function handlePlaidMicroDepositSuccess() {
    await refreshPageContentCollection();
    await refresh();
  }

  // plaid micro-deposit stuff
  const { handleClick: plaidLinkHandleClick, token: plaidToken } = usePlaidLink({
    onSuccess: handlePlaidMicroDepositSuccess,
    onExit: handlePlaidFlowStop,
    onLoad: handlePlaidFlowStart,
    onFailure: handlePlaidFlowStop,
    plaidTokenLink: activeMicroDepositPlaidTokenURL,
  });
  useEffect(() => {
    if (activeMicroDepositPlaidTokenURL) {
      plaidLinkHandleClick();
    }
  }, [plaidToken]);

  return (
    <Box className={classNames(classes.root, { [classes.rootDesktop]: !isMobile })}>
      <Title className={classes.title} text={resource.attr('title')} test={'wizard-step-title'} />
      <Subtitle className={classes.subtitle} text={resource.attr('subtitle')} test={'wizard-step-subtitle'} />
      <div className={classes.mainContent}>
        <DisabledOverlay disabled={isPlaidFlowActive}>
          <ResourceSummaryGroups
            resourceGroups={resource.attr('accounts')}
            onResourceClick={handleClickForAccount}
            onResourceActionClick={handleClickForAccountAction}
          />
        </DisabledOverlay>
      </div>


      <PlaidButton
        color="secondary"
        onSuccess={handlePlaidAddAccountSuccess}
        onExit={handlePlaidFlowStop}
        onLoad={handlePlaidFlowStart}
        onFailure={handlePlaidFlowStop}
        component={({ onClick, disabled }) => (
          <Button
            disabled={disabled || isPlaidFlowActive}
            onClick={onClick}
            test="client-account-funding-modal-add-new-ach-account"
            color="secondary"
            round
            startIcon={<AddCircleIcon color="secondary" />}
          >
            Add New Account
          </Button>)
        }
        plaidTokenLink={resource.relationship(fr.relationships.plaidToken.name).link('related').url()}
      />
    </Box>
  );
};

ApexFundingAccountListStep.propTypes = {
};

export default ApexFundingAccountListStep;
